import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export const LoadingScreen = ({ onComplete }) => {
  const [animationStage, setAnimationStage] = useState('drawing'); // drawing -> filling -> ready -> zooming -> complete
  const [scrollAmount, setScrollAmount] = useState(0);

  // Helper functions to get style values based on animation stage
  const getLogoOpacity = (stage) => {
    if (stage === 'drawing') return 0.4;
    if (stage === 'filling') return 0.8;
    return 1;
  };

  const getLogoScale = (stage) => {
    if (stage === 'drawing') return 'scale(0.92)';
    if (stage === 'filling') return 'scale(0.96)';
    return 'scale(1)';
  };

  const getLogoBrightness = (stage) => {
    if (stage === 'drawing') return '70';
    if (stage === 'filling') return '115';
    return '96';
  };

  const getLogoFilter = (stage) => {
    const brightness = getLogoBrightness(stage);
    const blur = stage === 'drawing' ? 'blur(1px)' : '';
    return `brightness(0) saturate(100%) invert(67%) sepia(46%) saturate(1342%) hue-rotate(141deg) brightness(${brightness}%) contrast(101%) ${blur}`;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Stage 1: Drawing (2s)
    const drawingTimer = setTimeout(() => {
      setAnimationStage('filling');
    }, 2000);

    // Stage 2: Filling (1s) -> then ready for scroll
    const fillingTimer = setTimeout(() => {
      setAnimationStage('ready');
    }, 3500);

    return () => {
      clearTimeout(drawingTimer);
      clearTimeout(fillingTimer);
    };
  }, []);
  // Note: This effect should only run once on mount, timers are local to this effect

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (animationStage !== 'ready' && animationStage !== 'zooming') return;

    let touchStartY = 0;
    let touchEndY = 0;

    const handleScroll = (e) => {
      e.preventDefault();
      
      const delta = e.deltaY || e.detail || e.wheelDelta;
      
      if (delta > 0) { // Scrolling down
        setScrollAmount(prev => {
          const newAmount = Math.min(prev + Math.abs(delta) * 0.5, 100);
          
          // When scroll reaches 30%, start zooming
          if (newAmount > 30 && animationStage === 'ready') {
            setAnimationStage('zooming');
          }
          
          // When scroll reaches 100%, complete
          if (newAmount >= 100) {
            setTimeout(() => {
              setAnimationStage('complete');
              onComplete();
            }, 500);
          }
          
          return newAmount;
        });
      }
    };

    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      touchEndY = e.touches[0].clientY;
      
      // Calculate swipe distance
      const swipeDistance = touchStartY - touchEndY;
      
      if (swipeDistance > 0) { // Swiping up (scrolling down)
        setScrollAmount(prev => {
          const newAmount = Math.min(prev + Math.abs(swipeDistance) * 0.3, 100);
          
          // When scroll reaches 30%, start zooming
          if (newAmount > 30 && animationStage === 'ready') {
            setAnimationStage('zooming');
          }
          
          // When scroll reaches 100%, complete
          if (newAmount >= 100) {
            setTimeout(() => {
              setAnimationStage('complete');
              onComplete();
            }, 500);
          }
          
          return newAmount;
        });
        
        // Reset touch start for continuous swiping
        touchStartY = touchEndY;
      }
    };

    // Add scroll listeners
    window.addEventListener('wheel', handleScroll, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [animationStage, onComplete]);
  // Note: Variables like delta, e, touchStartY are local to event handlers

  if (animationStage === 'complete') return null;

  const zoomScale = animationStage === 'zooming' ? 1 + (scrollAmount / 100) * 4 : 1;
  const containerOpacity = animationStage === 'zooming' ? Math.max(0, 1 - scrollAmount / 100) : 1;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#123a62] overflow-hidden"
      style={{
        opacity: containerOpacity,
        pointerEvents: animationStage === 'complete' ? 'none' : 'auto'
      }}
    >
      <div 
        className={`logo-animation-container ${animationStage}`}
        style={{
          transform: `scale(${zoomScale})`,
          transition: animationStage === 'zooming' ? 'none' : 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Logo with smooth progressive animation */}
        <div className="relative w-[400px] h-[400px] flex items-center justify-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_strategic-studio/artifacts/v82vnbid_QC%20-%20Logotipo%20-%20FINAL%20-%20Color.svg"
            alt="Quirófano Creativo"
            className="w-[300px] h-auto"
            style={{
              opacity: getLogoOpacity(animationStage),
              transform: getLogoScale(animationStage),
              filter: getLogoFilter(animationStage),
              transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          />
        </div>
      </div>

      {/* Scroll Indicator - Shows when ready */}
      {(animationStage === 'ready' || animationStage === 'zooming') && (
        <div 
          className="absolute bottom-20 text-center flex flex-col items-center gap-4"
          style={{
            opacity: animationStage === 'zooming' ? Math.max(0, 1 - scrollAmount / 50) : 1,
            transition: 'opacity 0.3s'
          }}
        >
          <p className="text-[#1987c9] text-sm font-light tracking-wider animate-pulse">
            Desliza para continuar
          </p>
          <div className="flex flex-col items-center animate-bounce">
            <ChevronDown className="w-8 h-8 text-[#00adb3]" />
          </div>
        </div>
      )}

      {/* Loading dots - Shows during drawing/filling */}
      {(animationStage === 'drawing' || animationStage === 'filling') && (
        <div className="absolute bottom-20 text-center">
          <div className="flex items-center gap-2 text-[#1987c9]">
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoadingScreen;
